'use client';

import {
  faArrowRightLong,
  faChevronLeft,
  faChevronRight,
  faMouse,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Typography } from '@mui/material';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/atoms';
import { getImageAlt, getImageUrl } from '@/services/utils';

// Banner data interface
export interface BannerData {
  id: number;
  title: string;
  subtitle: string;
  backgroundImage?: any; // Can be string or object
  ctaText: string;
  ctaLink: string;
  showNavigation: boolean;
  showMouseIndicator: boolean;
  overlayOpacity: number;
  order: number;
  isActive: boolean;
}

// Banner collection data interface
export interface BannerCollectionData {
  banners: BannerData[];
  autoPlay: boolean;
  autoPlayInterval: number;
  showNavigation: boolean;
  showMouseIndicator: boolean;
}

// Banner slider component props
interface BannerSliderProps {
  data: BannerCollectionData;
  className?: string;
  id?: string; // Optional ID for easier debugging
}

/**
 * Banner Slider Component - Multi-banner slider with MUI design system
 *
 * Features:
 * - Fixed height of 880px as per requirements
 * - MUI design system integration
 * - Multiple banners with circular navigation
 * - Auto-play functionality
 * - Responsive design
 * - SEO-friendly with proper alt text
 * - Circular navigation (end to beginning)
 * - Optimized for LCP performance
 */
const BannerSlider: React.FC<BannerSliderProps> = ({
  data,
  className = '',
  id,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Swipe functionality state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Mouse indicator fade state
  const [mouseIndicatorOpacity, setMouseIndicatorOpacity] = useState(1);

  // Filter active banners
  const activeBanners = data.banners.filter(banner => banner.isActive);
  const totalSlides = activeBanners.length;

  // Animation effect on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!data.autoPlay || totalSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, data.autoPlayInterval);

    return () => clearInterval(interval);
  }, [data.autoPlay, data.autoPlayInterval, totalSlides]);

  // Scroll detection for mouse indicator fade
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const bannerHeight = bannerRef.current?.offsetHeight || 880;

      // Start fading when scroll is 10% of banner height
      const fadeStart = bannerHeight * 0.1;
      // Completely fade when scroll is 50% of banner height
      const fadeEnd = bannerHeight * 0.5;

      if (scrollY <= fadeStart) {
        setMouseIndicatorOpacity(1);
      } else if (scrollY >= fadeEnd) {
        setMouseIndicatorOpacity(0);
      } else {
        // Calculate opacity between fadeStart and fadeEnd
        const opacity = 1 - (scrollY - fadeStart) / (fadeEnd - fadeStart);
        setMouseIndicatorOpacity(Math.max(0, opacity));
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle navigation with circular logic
  const handlePrevSlide = () => {
    setCurrentSlide(prev => (prev > 0 ? prev - 1 : totalSlides - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide(prev => (prev < totalSlides - 1 ? prev + 1 : 0));
  };

  // Handle slide indicator click
  const handleSlideIndicatorClick = (index: number) => {
    setCurrentSlide(index);
  };

  // Swipe functionality
  const minSwipeDistance = 50; // Minimum distance for swipe to be registered

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left - go to next slide
      setCurrentSlide(prev => (prev < totalSlides - 1 ? prev + 1 : 0));
    } else if (isRightSwipe) {
      // Swipe right - go to previous slide
      setCurrentSlide(prev => (prev > 0 ? prev - 1 : totalSlides - 1));
    }

    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
  };

  // Get current banner data
  const currentBanner = activeBanners[currentSlide];

  if (!currentBanner || totalSlides === 0) {
    return null;
  }

  // Get background image URL
  const backgroundImageUrl = getImageUrl(currentBanner.backgroundImage);
  const backgroundAltText = getImageAlt(currentBanner.backgroundImage);

  // If no background image is provided, don't render the banner
  if (!backgroundImageUrl) {
    return null;
  }

  return (
    <Box
      component='section'
      ref={bannerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      id={id} // Add ID for easier debugging
      data-component='banner-slider'
      data-slide={currentSlide}
      data-total-slides={totalSlides}
      data-active-banners={activeBanners.length}
      data-swipe-enabled='true'
      data-visible={isVisible}
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '70vh', sm: '75vh', md: '80vh', lg: '880px' }, // Responsive height for better mobile UX
        minHeight: { xs: '500px', sm: '600px' }, // Minimum height to prevent too small banners
        overflow: 'hidden',
        touchAction: 'pan-y', // Allow vertical scrolling but handle horizontal swipes
        userSelect: 'none', // Prevent text selection during swipes
      }}
      role='banner'
      aria-label='Hero banner slider'
      className={className}
    >
      {/* Swipe Feedback Indicator */}
      {isSwiping && touchStart && touchEnd && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 30,
            display: { xs: 'flex', lg: 'none' }, // Only show on mobile
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '8px',
            px: 2,
            py: 1,
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: 500,
            pointerEvents: 'none',
          }}
        >
          {touchStart - touchEnd > 0 ? '← Next' : 'Previous →'}
        </Box>
      )}

      {/* Optimized Background Image using Next.js Image component */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
        aria-hidden='true'
      >
        <Image
          src={backgroundImageUrl}
          alt={backgroundAltText}
          fill
          priority={currentSlide === 0} // Priority loading for first image (LCP optimization)
          quality={85} // Optimize quality for better performance
          sizes='100vw'
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          placeholder='blur'
          blurDataURL='data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k='
        />
      </Box>

      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'black',
          opacity: currentBanner.overlayOpacity || 0.6,
        }}
        aria-hidden='true'
      />

      {/* Content Container */}
      <Box
        data-component='banner-slider-content'
        data-current-banner={activeBanners[currentSlide]?.title}
        sx={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
        }}
      >
        <Box
          sx={{
            maxWidth: '7xl',
            mx: 'auto',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {/* Main Content */}
          <Box
            sx={{
              transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
              opacity: isVisible ? 1 : 0,
              transition: 'all 1s ease-out',
              ml: { xs: 3, sm: 4, md: '160px', lg: '345px' },
              mt: { xs: '15vh', sm: '12vh', md: '10vh', lg: '200px' },
              pr: { xs: 3, sm: 4, md: 6, lg: 0 },
            }}
          >
            {/* Title */}
            <Typography
              variant='h1'
              component='h1'
              sx={{
                color: 'white',
                textTransform: 'uppercase',
                letterSpacing: 'wide',
                mb: { xs: 2, lg: 3 },
                fontSize: {
                  xs: '1.25rem',
                  sm: '1.5rem',
                  md: '1.75rem',
                  lg: '24px',
                },
                fontWeight: 400,
                fontFamily: 'var(--font-inter), Inter, sans-serif',
                lineHeight: 1.21,
                width: { xs: '100%' },
              }}
            >
              {currentBanner.title}
            </Typography>

            {/* Subtitle */}
            <Typography
              variant='h2'
              component='h2'
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: { xs: 4, lg: 6 },
                fontSize: {
                  xs: '1.75rem',
                  sm: '2.25rem',
                  md: '3rem',
                  lg: '48px',
                },
                fontFamily: 'var(--font-inter), Inter, sans-serif',
                lineHeight: 1.21,
                width: { xs: '100%', lg: '705px' },
                maxWidth: '100%',
              }}
            >
              {currentBanner.subtitle}
            </Typography>

            {/* CTA Button */}
            <Button
              href={currentBanner.ctaLink}
              variant='primary'
              size='large'
              icon={
                <FontAwesomeIcon
                  icon={faArrowRightLong}
                  style={{ width: '16px', height: '16px' }}
                />
              }
              iconPosition='end'
              sx={{
                backgroundColor: 'secondary.main',
                color: 'white',
                px: { xs: 3, lg: 4 },
                py: { xs: 1.5, lg: 2 },
                fontSize: { xs: '1rem', sm: '1.125rem', lg: '18px' },
                lineHeight: 1.3,
                width: { xs: 'auto', lg: '223px' },
                height: { xs: '44px', lg: '56px' },
                minWidth: { xs: '160px', sm: '180px' }, // Ensure minimum button width on mobile
                '&:hover': {
                  backgroundColor: 'secondary.dark',
                },
              }}
            >
              {currentBanner.ctaText}
            </Button>
          </Box>

          {/* Navigation Dots and Arrows - Now part of content layout */}
          {data.showNavigation && totalSlides > 1 && (
            <Box
              data-component='banner-slider-navigation'
              data-current-slide={currentSlide}
              sx={{
                display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on medium screens and up
                alignItems: 'center',
                gap: 2,
                ml: { xs: 3, sm: 4, md: '160px', lg: '345px' }, // Match main content alignment
                mb: { xs: '8vh', sm: '6vh', md: '5vh', lg: '118px' }, // Responsive bottom margin
                pr: { xs: 3, sm: 4, md: 6, lg: 0 }, // Match main content padding
              }}
            >
              {/* Slide Counter */}
              <Typography
                variant='body2'
                sx={{
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: 'wide',
                  fontSize: { xs: '0.75rem', lg: '0.875rem' },
                  fontFamily: 'var(--font-inter), Inter, sans-serif',
                  fontWeight: 400,
                  lineHeight: 1.21,
                  width: { xs: 'auto', lg: '38px' },
                }}
              >
                {currentSlide + 1} - {totalSlides}
              </Typography>

              {/* Progress Bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: { xs: '120px', sm: '180px', lg: '240px' },
                    height: '2px',
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      backgroundColor: 'white',
                      transition: 'width 0.5s ease-out',
                      width: `${((currentSlide + 1) / totalSlides) * 100}%`,
                    }}
                  />
                </Box>
              </Box>

              {/* Navigation Arrows */}
              <IconButton
                onClick={handlePrevSlide}
                sx={{
                  color: 'white',
                  p: 1,
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    color: 'secondary.main',
                  },
                }}
                aria-label='Previous slide'
              >
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  style={{ width: '16px', height: '16px' }}
                />
              </IconButton>
              <IconButton
                onClick={handleNextSlide}
                sx={{
                  color: 'white',
                  p: 1,
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    color: 'secondary.main',
                  },
                }}
                aria-label='Next slide'
              >
                <FontAwesomeIcon
                  icon={faChevronRight}
                  style={{ width: '16px', height: '16px' }}
                />
              </IconButton>
            </Box>
          )}

          {/* Right-side elements container */}
          <Box
            sx={{
              position: 'absolute',
              right: { xs: 4, lg: '68px' },
              bottom: { xs: '4vh', sm: '3vh', md: '2vh', lg: '80px' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 2,
            }}
          >
            {/* Mouse Scroll Indicator */}
            {data.showMouseIndicator && (
              <Box
                data-component='banner-slider-mouse-indicator'
                sx={{
                  display: { xs: 'none', lg: 'flex' }, // Hide on mobile, show on large screens
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  opacity: mouseIndicatorOpacity,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: mouseIndicatorOpacity > 0 ? 'auto' : 'none',
                }}
              >
                <Box
                  sx={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FontAwesomeIcon
                    icon={faMouse}
                    style={{ width: '24px', height: '24px', color: 'white' }}
                  />
                </Box>
                <Box
                  sx={{
                    width: '1px',
                    height: '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        opacity: 1,
                      },
                      '50%': {
                        opacity: 0.5,
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Mobile Slide Indicators */}
      {totalSlides > 1 && (
        <Box
          data-component='banner-slider-mobile-indicators'
          data-total-indicators={totalSlides}
          sx={{
            position: 'absolute',
            bottom: { xs: '4vh', sm: '3vh' }, // Responsive bottom positioning
            left: '50%',
            transform: 'translateX(-50%)',
            display: { xs: 'flex', md: 'none' }, // Show only on mobile
            gap: 1,
            zIndex: 20,
          }}
        >
          {activeBanners.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor:
                  index === currentSlide ? 'white' : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                '&:hover': {
                  backgroundColor:
                    index === currentSlide
                      ? 'white'
                      : 'rgba(255, 255, 255, 0.75)',
                },
              }}
              onClick={() => handleSlideIndicatorClick(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BannerSlider;
